/**
 * MoistureEquipmentTracker
 *
 * Technician-facing UI for tracking equipment deployed on a moisture-mapping
 * job. Backed by /api/moisture/{request_id}/equipment.
 *
 * Features:
 *  - Lists current equipment with REC vs DEP quantities and variance.
 *  - "Seed from AI" button — pulls equipment_recommended strings from the
 *    latest moisture analysis and creates EquipmentItem rows for the canon
 *    types we recognise (LGR Dehu / Air Mover / HEPA / etc.).
 *  - Add row / edit row (qty deployed + serial numbers + placement notes).
 *  - Delete row.
 */
import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Wrench, Loader2, Plus, Trash2, Save, Sparkles, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAI } from "@/lib/ai";

const EQUIPMENT_TYPES = [
  "LGR Dehumidifier", "Refrigerant Dehumidifier", "Desiccant Dehumidifier",
  "Axial Air Mover", "Centrifugal Air Mover", "HEPA Air Scrubber",
  "Heater", "Negative Air Machine", "Drying Chamber", "Other",
];

/** Best-effort: parse an AI recommendation string like "2 LGR dehus, 4 air movers"
 *  into EquipmentItem rows. */
function parseAIRecommendation(strs) {
  const items = [];
  for (const s of (strs || [])) {
    const low = s.toLowerCase();
    // Pick the canonical type that matches
    let type = "Other";
    if (/lgr.*dehu/.test(low))            type = "LGR Dehumidifier";
    else if (/refrigerant.*dehu/.test(low)) type = "Refrigerant Dehumidifier";
    else if (/desiccant/.test(low))        type = "Desiccant Dehumidifier";
    else if (/axial.*air mover/.test(low)) type = "Axial Air Mover";
    else if (/centrifugal.*air mover/.test(low)) type = "Centrifugal Air Mover";
    else if (/air mover/.test(low))        type = "Axial Air Mover";
    else if (/hepa/.test(low))             type = "HEPA Air Scrubber";
    else if (/heater/.test(low))           type = "Heater";
    else if (/negative.*air/.test(low))    type = "Negative Air Machine";
    else if (/drying.*chamber/.test(low))  type = "Drying Chamber";
    // qty from "2 LGR dehus" / "x 4" / "(3)"
    const m = s.match(/(\d+)/);
    const qty = m ? parseInt(m[1]) : 1;
    items.push({ equipment_type: type, qty_recommended: qty, qty_deployed: 0,
                 serial_numbers: [], placement_notes: s, source: "ai" });
  }
  return items;
}

export default function MoistureEquipmentTracker({ requestId, aiAnalysis }) {
  const { aiOn } = useAI();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get(`/moisture/${requestId}/equipment`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load equipment");
    } finally { setLoading(false); }
  }, [requestId]);
  useEffect(() => { load(); }, [load]);

  const seedFromAI = async () => {
    const recs = aiAnalysis?.equipmentRecommended || [];
    if (recs.length === 0) {
      toast.info("AI hasn't recommended any equipment yet — run analysis first.");
      return;
    }
    setLoading(true);
    try {
      const parsed = parseAIRecommendation(recs);
      await API.post(`/moisture/${requestId}/equipment/seed-from-ai`,
                     { items: parsed });
      toast.success(`Seeded ${parsed.length} item(s) from AI`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seed failed");
      setLoading(false);
    }
  };

  const saveDraft = async () => {
    if (!draft) return;
    setAdding(true);
    try {
      const qtyDep = parseInt(draft.qty_deployed) || 0;
      if (draft.id) {
        await API.patch(`/moisture/${requestId}/equipment/${draft.id}`, {
          qty_deployed: qtyDep,
          serial_numbers: (draft.serial_numbers || []).filter(Boolean),
          placement_notes: draft.placement_notes || "",
          deployed_at: qtyDep > 0 ? new Date().toISOString() : null,
        });
        toast.success("Updated");
      } else {
        await API.post(`/moisture/${requestId}/equipment`, {
          equipment_type: draft.equipment_type,
          qty_recommended: parseInt(draft.qty_recommended) || 0,
          qty_deployed: qtyDep,
          serial_numbers: (draft.serial_numbers || []).filter(Boolean),
          placement_notes: draft.placement_notes || "",
          deployed_at: qtyDep > 0 ? new Date().toISOString() : null,
          source: "manual",
        });
        toast.success("Added");
      }
      setDraft(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setAdding(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this equipment record?")) return;
    try {
      await API.delete(`/moisture/${requestId}/equipment/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-4">
      <Loader2 className="w-4 h-4 animate-spin text-cyan-500" />
    </div>
  );

  return (
    <div className="space-y-2" data-testid="moisture-equipment-tracker">
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-[#64748b] uppercase font-bold flex items-center gap-1">
          <Wrench className="w-3 h-3" /> Equipment Tracking ({items.length})
        </p>
        <div className="flex gap-1">
          {aiOn && aiAnalysis?.equipmentRecommended?.length > 0 && (
            <Button onClick={seedFromAI} size="sm" variant="outline"
                    className="text-[10px] h-7 px-2 border-cyan-700/40 text-cyan-300"
                    data-testid="eq-seed-ai">
              <Sparkles className="w-3 h-3 mr-1" /> Seed from AI
            </Button>
          )}
          <Button onClick={() => setDraft({ equipment_type: "Axial Air Mover",
                                            qty_recommended: 1, qty_deployed: 0,
                                            serial_numbers: [""], placement_notes: "" })}
                  size="sm" variant="outline"
                  className="text-[10px] h-7 px-2 border-emerald-700/40 text-emerald-300"
                  data-testid="eq-add-btn">
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
      </div>

      {items.length === 0 && !draft && (
        <p className="text-[10px] italic text-[#475569] text-center py-2">
          No equipment tracked yet.
        </p>
      )}

      <div className="space-y-1.5">
        {items.map(it => {
          const variance = (it.qty_deployed || 0) - (it.qty_recommended || 0);
          const tone = variance === 0 ? "text-emerald-400"
                     : Math.abs(variance) > 1 ? "text-red-400" : "text-amber-400";
          return (
            <div key={it.id} className="bg-[#0f172a] border border-[#1e293b] rounded p-2"
                 data-testid={`eq-row-${it.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-[#e2e8f0] truncate">
                    {it.equipment_type}
                  </p>
                  <p className="text-[9px] text-[#64748b]">
                    REC {it.qty_recommended} · DEP {it.qty_deployed} ·{" "}
                    <span className={tone}>VAR {variance > 0 ? `+${variance}` : variance}</span>
                    {it.source === "ai" && " · AI"}
                  </p>
                </div>
                <Button onClick={() => setDraft({
                  id: it.id,
                  equipment_type: it.equipment_type,
                  qty_recommended: it.qty_recommended,
                  qty_deployed: it.qty_deployed,
                  serial_numbers: it.serial_numbers?.length ? it.serial_numbers : [""],
                  placement_notes: it.placement_notes || "",
                })}
                        size="sm" variant="ghost"
                        className="h-7 text-[10px] text-cyan-400 hover:bg-cyan-900/20"
                        data-testid={`eq-edit-${it.id}`}>
                  Edit
                </Button>
                <Button onClick={() => remove(it.id)} size="sm" variant="ghost"
                        className="h-7 text-red-400 hover:bg-red-900/20"
                        data-testid={`eq-delete-${it.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
              {it.placement_notes && (
                <p className="text-[10px] text-[#94a3b8] mt-1 line-clamp-2">{it.placement_notes}</p>
              )}
            </div>
          );
        })}
      </div>

      {draft && (
        <div className="bg-[#020617] border border-cyan-700/40 rounded p-2 space-y-2"
             data-testid="eq-draft">
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-cyan-300 font-bold">
              {draft.id ? "Edit" : "Add"} equipment
            </p>
            <Button onClick={() => setDraft(null)} size="sm" variant="ghost"
                    className="h-6 text-[10px] text-[#64748b]">
              <X className="w-3 h-3" />
            </Button>
          </div>
          {!draft.id && (
            <Select value={draft.equipment_type}
                    onValueChange={v => setDraft({ ...draft, equipment_type: v })}>
              <SelectTrigger className="h-7 text-xs bg-[#0f172a] border-[#1e293b] text-[#e2e8f0]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EQUIPMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] text-[#64748b] uppercase">REC qty</label>
              <Input type="number" value={draft.qty_recommended}
                     disabled={!!draft.id}
                     onChange={e => setDraft({ ...draft, qty_recommended: e.target.value })}
                     className="h-7 text-xs bg-[#0f172a] border-[#1e293b] text-[#e2e8f0]"
                     data-testid="eq-qty-rec" />
            </div>
            <div>
              <label className="text-[9px] text-[#64748b] uppercase">DEP qty</label>
              <Input type="number" value={draft.qty_deployed}
                     onChange={e => setDraft({ ...draft, qty_deployed: e.target.value })}
                     className="h-7 text-xs bg-[#0f172a] border-[#1e293b] text-[#e2e8f0]"
                     data-testid="eq-qty-dep" />
            </div>
          </div>
          <div>
            <label className="text-[9px] text-[#64748b] uppercase">Serial #s (comma sep)</label>
            <Input value={(draft.serial_numbers || []).join(", ")}
                   onChange={e => setDraft({ ...draft,
                     serial_numbers: e.target.value.split(",").map(s => s.trim()) })}
                   placeholder="SN-001, SN-002"
                   className="h-7 text-xs bg-[#0f172a] border-[#1e293b] text-[#e2e8f0]"
                   data-testid="eq-serials" />
          </div>
          <div>
            <label className="text-[9px] text-[#64748b] uppercase">Placement notes</label>
            <Input value={draft.placement_notes}
                   onChange={e => setDraft({ ...draft, placement_notes: e.target.value })}
                   placeholder="Center of room, on tile"
                   className="h-7 text-xs bg-[#0f172a] border-[#1e293b] text-[#e2e8f0]"
                   data-testid="eq-notes" />
          </div>
          <Button onClick={saveDraft} disabled={adding}
                  size="sm"
                  className="w-full h-7 bg-cyan-600 hover:bg-cyan-700 text-white text-[11px]"
                  data-testid="eq-save">
            {adding ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <><Save className="w-3 h-3 mr-1" /> Save</>}
          </Button>
        </div>
      )}
    </div>
  );
}
